import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { getUserClassroomAssignmentsQuestions, submitUserClassroomAssignmentResponse } from '../../../services/userService';
import { decodeData } from '../../../utils/encodeAndEncode';
import { useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';

function AssignmentQuestions() {
    const [selectedFiles, setSelectedFiles] = useState({});
    const [answerTexts, setAnswerTexts] = useState({});
    const [questions, setQuestions] = useState([]);
    const [submittedQuestions, setSubmittedQuestions] = useState(new Set());
    const [dueDate, setDueDate] = useState(null);
    const [classroomName, setClassroomName] = useState('');

    const { encodedAssignmentId } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const encodedClassroomId = searchParams.get('classroomid');
    const classroomNameFromState = location.state?.classroomName || '';

    // Format due date
    const formatDueDate = (dueDate) => {
        if (!dueDate) return 'No due date';
        
        const date = new Date(dueDate);
        if (isNaN(date.getTime())) return 'Invalid date';
        
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check if due date has passed
    const checkDueDatePassed = (dueDate) => {
        if (!dueDate) return false;
        const date = new Date(dueDate);
        if (isNaN(date.getTime())) return false;
        return date < new Date();
    };

    const assignment_id = decodeData(decodeURIComponent(encodedAssignmentId));
    const classroom_id = encodedClassroomId ? decodeData(decodeURIComponent(encodedClassroomId)) : null;
    const user_id = JSON.parse(localStorage.getItem('user'))?.id;

    // Fetch Questions
    const fetchQuestions = async () => {
        try {
            const response = await getUserClassroomAssignmentsQuestions({
                assignment_id,
                classroom_id,
                user_id
            });

            if (response.success) {
                setQuestions(response.data);
                setDueDate(response.due_date);
                setClassroomName(response.classroom_name || classroomNameFromState || '');
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
        }
    };

    useEffect(() => {
        if (assignment_id && classroom_id && user_id) {
            fetchQuestions();
        }
    }, [assignment_id, classroom_id, user_id]);

    // Handle File Upload
    const handleFileUpload = (event, type, questionId) => {
        if (submittedQuestions.has(questionId)) return;

        const file = event.target.files[0];
        if (file) {
            if (type === 'image' && !file.type.startsWith('image/')) {
                alert('Please upload an image file');
                return;
            }
            if (type === 'audio' && !file.type.startsWith('audio/')) {
                alert('Please upload an audio file');
                return;
            }
            setSelectedFiles(prev => ({
                ...prev,
                [questionId]: {
                    file,
                    preview: type === 'image' ? URL.createObjectURL(file) : null
                }
            }));
        }
    };

    const handleDeleteFile = (questionId) => {
        setSelectedFiles(prev => {
            const newState = { ...prev };
            if (newState[questionId]?.preview) {
                URL.revokeObjectURL(newState[questionId].preview);
            }
            delete newState[questionId];
            return newState;
        });
    };

    // Handle Text Change
    const handleTextChange = (value, questionId) => {
        const isDueDatePassed = checkDueDatePassed(dueDate);
        
        if (!submittedQuestions.has(questionId) && !isDueDatePassed) {
            setAnswerTexts(prev => ({
                ...prev,
                [questionId]: value
            }));
        }
    };

    const handleSubmitAnswer = async (question) => {
        if (submittedQuestions.has(question.id)) return;

        const formData = new FormData();
        formData.append('classroom_id', classroom_id);
        formData.append('assignment_id', assignment_id);
        formData.append('question_id', question.id);
        formData.append('user_id', user_id);
        formData.append('response_type', question.response_type);

        if (question.response_type === 'text_response') {
            formData.append('answer_text', answerTexts[question.id] || '');
        } else {
            const file = selectedFiles[question.id];
            if (file) {
                formData.append('media', file.file);
            }
        }

        try {
            const response = await submitUserClassroomAssignmentResponse(formData);
            if (response.success) {
                // Mark question as submitted locally
                setSubmittedQuestions(prev => new Set([...prev, question.id]));

                // Clear the response after submission
                setAnswerTexts(prev => {
                    const newState = { ...prev };
                    delete newState[question.id];
                    return newState;
                });
                setSelectedFiles(prev => {
                    const newState = { ...prev };
                    delete newState[question.id];
                    return newState;
                });

                // Refresh questions data to get updated status
                await fetchQuestions();
            }
        } catch (error) {
            console.error("Error submitting answer:", error);
        }
    };

    const renderQuestionMedia = (question) => {
        switch (question.question_type) {
            case 'image':
                return (
                    <div className="mb-3">
                        <img
                            src={question.media_url}
                            alt="Question"
                            className="w-full rounded"
                            style={{ maxHeight: '300px' }}
                        />
                    </div>
                );
            case 'audio':
                return (
                    <div className="mb-3">
                        <audio controls className="w-full">
                            <source src={question.media_url} type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                );
            default:
                return null;
        }
    };

    const renderFilePreview = (question) => {
        const fileData = selectedFiles[question.id];
        if (!fileData) return null;

        return (
            <div className="mt-3 bg-gray-100 rounded p-2">
                {question.response_type === 'image_response' && fileData.preview && (
                    <>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-500">Selected Image:</span>
                            <button
                                className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-medium py-1 px-3 rounded-lg text-sm flex items-center gap-1"
                                style={{
                                    width: 'auto',
                                    padding: '4px 12px',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={() => handleDeleteFile(question.id)}
                            >
                                <Icon icon="mdi:delete" width="18" height="18" />
                                Remove
                            </button>
                        </div>
                        <img
                            src={fileData.preview}
                            alt="Preview"
                            className="w-full rounded"
                            style={{ maxHeight: '200px' }}
                        />
                    </>
                )}
                {question.response_type === 'audio_response' && fileData.file && (
                    <div className="relative bg-gray-100 rounded p-3">
                        {/* Top-right aligned small Remove button */}
                        <div className="flex justify-end mb-2">
                            <button
                                className="bg-red-500 hover:bg-red-600 text-white font-medium py-1 px-3 rounded-lg text-sm flex items-center gap-1"
                                style={{
                                    width: 'auto',
                                    padding: '4px 12px',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={() => handleDeleteFile(question.id)}
                            >
                                <Icon icon="mdi:delete" width="14" height="14" />
                                Remove
                            </button>
                        </div>

                        {/* File Name */}
                        <div className="flex items-center mb-2">
                            <Icon icon="mdi:file-music-outline" className="text-primary mr-2" width="20" height="20" />
                            <span className="text-gray-500 truncate" style={{ maxWidth: '250px' }}>
                                {fileData.file.name}
                            </span>
                        </div>

                        {/* Audio Player */}
                        <audio controls className="w-full">
                            <source src={URL.createObjectURL(fileData.file)} type={fileData.file.type} />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                )}
            </div>
        );
    };

    const renderAnswerSection = (question) => {
        // Check if due date has passed
        const isDueDatePassed = checkDueDatePassed(dueDate);

        if (question.is_completed === 1 || submittedQuestions.has(question.id)) {
            return (
                <div className="p-3 bg-gray-100 rounded">
                    <div className="flex items-center mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border border-green-500 text-green-500 bg-green-50 gap-2 px-3 py-2">
                            <Icon icon="mdi:check-circle" width="14" height="14" />
                            Response Submitted
                        </span>
                    </div>
                    {question.answer_text && (
                        <p className="mb-0">{question.answer_text}</p>
                    )}
                    {question.submission_media_url && (
                        question.submitted_response_type === 'image_response' ? (
                            <img
                                src={question.submission_media_url}
                                alt="Submitted Answer"
                                className="w-full rounded mt-2"
                                style={{ maxHeight: '200px' }}
                            />
                        ) : (
                            <audio controls className="w-full mt-2">
                                <source src={question.submission_media_url} type="audio/mpeg" />
                                Your browser does not support the audio element.
                            </audio>
                        )
                    )}
                </div>
            );
        }

        if (isDueDatePassed) {
            return (
                <div className="p-3 bg-gray-100 rounded">
                    <div className="flex items-center mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border border-red-500 text-red-500 bg-red-50 gap-2 px-3 py-2">
                            <Icon icon="mdi:clock-alert" width="14" height="14" />
                            Submission Time Over
                        </span>
                    </div>
                    <p className="text-red-500 text-sm mb-0">
                        You cannot submit answers after the due date. The submission deadline has passed.
                    </p>
                </div>
            );
        }

        return (
            <div>
                {question.response_type === 'text_response' && (
                    <div className="mb-3">
                        <textarea
                            className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                            rows="3"
                            placeholder="Type your answer here..."
                            value={answerTexts[question.id] || ''}
                            onChange={(e) => handleTextChange(e.target.value, question.id)}
                            disabled={submittedQuestions.has(question.id) || isDueDatePassed}
                        ></textarea>
                    </div>
                )}

                {(question.response_type === 'image_response' || question.response_type === 'audio_response') && (
                    <div className="flex justify-start">
                        <div className="w-full md:w-1/4">
                            <div className="flex gap-2 mb-3">
                                <button
                                    className="border border-primary text-primary hover:bg-primary hover:text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2"
                                    onClick={() => document.getElementById(`file-upload-${question.id}`).click()}
                                    disabled={submittedQuestions.has(question.id) || selectedFiles[question.id] || isDueDatePassed}
                                >
                                    <Icon
                                        icon={question.response_type === 'image_response' ? 'mdi:image' : 'mdi:microphone'}
                                        width="20"
                                        height="20"
                                    />
                                    Upload {question.response_type === 'image_response' ? 'Image' : 'Audio'}
                                    <input
                                        id={`file-upload-${question.id}`}
                                        type="file"
                                        accept={question.response_type === 'image_response' ? 'image/*' : 'audio/*'}
                                        onChange={(e) => handleFileUpload(e, question.response_type === 'image_response' ? 'image' : 'audio', question.id)}
                                        style={{ display: 'none' }}
                                        disabled={submittedQuestions.has(question.id) || selectedFiles[question.id] || isDueDatePassed}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* File Preview Section */}
                {renderFilePreview(question)}

                <div className="flex justify-end mt-3">
                    <div className="w-full md:w-1/4">
                        <button
                            className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg text-sm w-full"
                            onClick={() => handleSubmitAnswer(question)}
                            disabled={
                                submittedQuestions.has(question.id) ||
                                isDueDatePassed ||
                                (question.response_type !== 'text_response' && !selectedFiles[question.id]) ||
                                (question.response_type === 'text_response' && !answerTexts[question.id])
                            }
                        >
                            Submit Answer
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full">
            {/* Back Button */}
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#7083FF] transition-colors py-2 px-2 rounded-lg hover:bg-white mb-4"
            >
                <Icon icon="mdi:arrow-left" width="20" height="20" />
                Back
            </button>

            {/* Header with Session Name and Question Stats */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <div className="min-w-0">
                    <h1 className="text-lg sm:text-xl font-semibold text-gray-800">Assignment Questions</h1>
                    {classroomName && (
                        <p className="text-sm text-gray-500 mt-1">{classroomName}</p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs border border-blue-200 text-blue-700 bg-blue-50">
                        <Icon icon="mdi:calendar" width="14" height="14" />
                        <span className="whitespace-nowrap">Due: {formatDueDate(dueDate)}</span>
                    </span>

                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs border border-primary/20 text-primary bg-primary/10">
                        <Icon icon="mdi:help-circle" width="14" height="14" />
                        <span className="whitespace-nowrap">{questions.length} Questions</span>
                    </span>

                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs border border-green-200 text-green-700 bg-green-50">
                        <Icon icon="mdi:check-circle" width="14" height="14" />
                        <span className="whitespace-nowrap">{questions.filter(q => q.is_completed === 1).length} Submitted</span>
                    </span>

                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs border border-yellow-200 text-yellow-800 bg-yellow-50">
                        <Icon icon="mdi:clock" width="14" height="14" />
                        <span className="whitespace-nowrap">{questions.filter(q => q.is_completed === 0).length} Remaining</span>
                    </span>
                </div>
            </div>

            {/* No Questions State */}
            {questions.length === 0 && (
                <div className="max-w-xl mx-auto">
                    <div className="bg-white border rounded-2xl shadow-sm">
                        <div className="text-center p-6 sm:p-8">
                            <div className="flex justify-center mb-4">
                                <Icon
                                    icon="mdi:file-document-outline"
                                    className="text-gray-400"
                                    width="72"
                                    height="72"
                                />
                            </div>
                            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">No Assignment Questions Available</h2>
                            <p className="text-sm text-gray-600 mb-5">The instructor hasn't added any questions to this assignment yet.</p>
                            <button
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90"
                                onClick={() => window.history.back()}
                            >
                                <Icon icon="mdi:arrow-left" width="18" height="18" />
                                Back to Assignment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Questions List */}
            {questions.length > 0 && questions.map((question) => (
                <div key={question.id} className="bg-white border rounded-2xl shadow-sm mb-4">
                    <div className="p-4 sm:p-5">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div className="min-w-0">
                                <h2 className="text-sm sm:text-base font-semibold text-gray-800 truncate">Question {question.question_order}</h2>
                            </div>

                            {question.is_completed === 1 && (
                                <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs border whitespace-nowrap ${
                                    question.answer_status === 'right'
                                        ? 'border-green-200 text-green-700 bg-green-50'
                                        : question.answer_status === 'wrong'
                                            ? 'border-red-200 text-red-700 bg-red-50'
                                            : 'border-yellow-200 text-yellow-800 bg-yellow-50'
                                }`}>
                                    <Icon
                                        icon={
                                            question.answer_status === 'right' ? 'mdi:check-circle' :
                                                question.answer_status === 'wrong' ? 'mdi:close-circle' :
                                                    'mdi:clock-outline'
                                        }
                                        width="14"
                                        height="14"
                                    />
                                    {question.answer_status === 'right' ? 'Correct' :
                                        question.answer_status === 'wrong' ? 'Incorrect' :
                                            'Pending Review'}
                                </span>
                            )}
                        </div>

                        <div className="mb-4">
                            <p className="text-sm text-gray-800">{question.question_text}</p>
                            {renderQuestionMedia(question)}
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold text-gray-800 mb-3">Your Response</h3>
                            {renderAnswerSection(question)}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default AssignmentQuestions;